import React, { useState, useEffect } from 'react';
import { X, Trash2, Save, XCircle, Loader2, Car, User, Hash } from 'lucide-react';
import vehicleService from '../../../services/vehicleService';
import { getImageUrl } from '../../../utils/imageUtils';

const VehicleDeletionActionModal = ({ vehicleId, isOpen, onClose, onSuccess }) => {
  const [vehicle, setVehicle] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && vehicleId) {
      loadVehicleDetails();
    }
  }, [isOpen, vehicleId]);

  // Désactiver le scroll du body quand le modal est ouvert
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  const loadVehicleDetails = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await vehicleService.getVehicleById(vehicleId);
      if (response.success) {
        setVehicle(response.data);
      } else {
        setError('Impossible de charger les détails du véhicule');
      }
    } catch (err) {
      setError(err.message || 'Erreur lors du chargement des détails');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePermanent = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer définitivement ce véhicule ? Cette action est irréversible.')) {
      return;
    }

    setIsProcessing(true);
    setError(null);
    try {
      await vehicleService.deleteVehiclePermanent(vehicleId);
      if (onSuccess) onSuccess('Véhicule supprimé définitivement');
      onClose();
    } catch (err) {
      setError(err.message || 'Erreur lors de la suppression');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteWithLogs = async () => {
    if (!window.confirm('Sauvegarder les logs et supprimer définitivement ce véhicule ? Cette action est irréversible.')) {
      return;
    }

    setIsProcessing(true);
    setError(null);
    try {
      await vehicleService.deleteVehicleWithLogs(vehicleId);
      if (onSuccess) onSuccess('Logs sauvegardés et véhicule supprimé définitivement');
      onClose();
    } catch (err) {
      setError(err.message || 'Erreur lors de la sauvegarde et suppression');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Rejeter la demande de suppression et réactiver le véhicule ?')) {
      return;
    }

    setIsProcessing(true);
    setError(null);
    try {
      await vehicleService.rejectVehicleDeletion(vehicleId);
      if (onSuccess) onSuccess('Demande de suppression rejetée. Le véhicule est à nouveau actif.');
      onClose();
    } catch (err) {
      setError(err.message || 'Erreur lors du rejet de la demande');
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  const vehicleImage = vehicle?.images && vehicle.images.length > 0 
    ? getImageUrl(vehicle.images[0]) 
    : null;
  
  const ownerName = vehicle?.owner?.vendorDetails?.companyName 
    || vehicle?.owner?.fullName 
    || vehicle?.owner?.email 
    || 'Propriétaire inconnu';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header avec les 3 boutons d'action */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-900">
              Demande de suppression de véhicule
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={isProcessing}
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          {/* Les 3 boutons d'action */}
          <div className="flex gap-3">
            {/* Bouton Supprimer (Rouge) */}
            <button
              onClick={handleDeletePermanent}
              disabled={isLoading || isProcessing}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Trash2 className="w-5 h-5" />
              )}
              Supprimer
            </button>

            {/* Bouton Sauvegarder et Supprimer (Bleu) */}
            <button
              onClick={handleDeleteWithLogs}
              disabled={isLoading || isProcessing}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Save className="w-5 h-5" />
              )}
              Sauvegarder et Supprimer
            </button>

            {/* Bouton Cancel (Gris) */}
            <button
              onClick={handleCancel}
              disabled={isLoading || isProcessing}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-500 hover:bg-gray-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <XCircle className="w-5 h-5" />
              )}
              Annuler
            </button>
          </div>
        </div>

        {/* Body avec les détails du véhicule */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#27489E]" />
              <span className="ml-3 text-gray-600">Chargement des détails...</span>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">{error}</p>
            </div>
          ) : vehicle ? (
            <div className="space-y-6">
              {/* Photo du véhicule */}
              <div className="flex justify-center">
                {vehicleImage ? (
                  <img
                    src={vehicleImage}
                    alt={vehicle.title || 'Véhicule'}
                    className="w-full max-w-md h-64 object-cover rounded-lg shadow-md"
                  />
                ) : (
                  <div className="w-full max-w-md h-64 bg-gray-200 rounded-lg flex items-center justify-center">
                    <Car className="w-16 h-16 text-gray-400" />
                  </div>
                )}
              </div>

              {/* Informations du véhicule */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Titre */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Car className="w-5 h-5 text-[#27489E]" />
                    <h3 className="font-semibold text-gray-700">Titre</h3>
                  </div>
                  <p className="text-gray-900 font-medium">
                    {vehicle.title || 'N/A'}
                  </p>
                </div>

                {/* Immatriculation */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Hash className="w-5 h-5 text-[#27489E]" />
                    <h3 className="font-semibold text-gray-700">Immatriculation</h3>
                  </div>
                  <p className="text-gray-900 font-medium">
                    {vehicle.licencePlateNumber || 'N/A'}
                  </p>
                </div>

                {/* Propriétaire */}
                <div className="bg-gray-50 rounded-lg p-4 md:col-span-2">
                  <div className="flex items-center gap-2 mb-2">
                    <User className="w-5 h-5 text-[#27489E]" />
                    <h3 className="font-semibold text-gray-700">Propriétaire</h3>
                  </div>
                  <p className="text-gray-900 font-medium">
                    {ownerName}
                  </p>
                  {vehicle?.owner?.email && (
                    <p className="text-sm text-gray-600 mt-1">
                      {vehicle.owner.email}
                    </p>
                  )}
                </div>
              </div>

              {/* Informations supplémentaires */}
              {(vehicle.specs?.brand || vehicle.specs?.model || vehicle.specs?.year) && (
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <h3 className="font-semibold text-gray-700 mb-2">Détails techniques</h3>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    {vehicle.specs?.brand?.name && (
                      <div>
                        <span className="text-gray-600">Marque:</span>
                        <span className="ml-2 font-medium text-gray-900">{vehicle.specs.brand.name}</span>
                      </div>
                    )}
                    {vehicle.specs?.model?.name && (
                      <div>
                        <span className="text-gray-600">Modèle:</span>
                        <span className="ml-2 font-medium text-gray-900">{vehicle.specs.model.name}</span>
                      </div>
                    )}
                    {vehicle.specs?.year && (
                      <div>
                        <span className="text-gray-600">Année:</span>
                        <span className="ml-2 font-medium text-gray-900">{vehicle.specs.year}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default VehicleDeletionActionModal;
